/**
 * Parseur de saisie naturelle du composer de tâches.
 * « Conclusions !1 #urgent @julien vendredi » → titre + attributs.
 * Pur et total (jamais d'exception) ; `now` injecté pour des tests
 * déterministes. Les jetons bruts (`raw`) sont renvoyés pour que la croix
 * d'un chip puisse retirer le mot du texte.
 */
import { normalizeSearch } from './taskSearch';

export interface ParseContext {
  tags: { id: string; name: string }[];
  collaborators: { id: string; full_name: string }[];
  now: Date;
  allowTags?: boolean;
  allowPriority?: boolean;
}

export interface ParsedTaskInput {
  title: string;
  priority: number | null;
  priorityRaw: string | null;
  dueDate: string | null;
  dueRaw: string | null;
  tagMatches: { id: string; name: string; raw: string }[];
  unknownTags: { name: string; raw: string }[];
  assigneeMatches: { id: string; full_name: string; raw: string }[];
}

const WEEKDAYS: Record<string, number> = {
  dimanche: 0, lundi: 1, mardi: 2, mercredi: 3, jeudi: 4, vendredi: 5, samedi: 6,
};

function toIsoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Interprète le dernier jeton comme une date. null si ce n'en est pas une. */
function parseDateToken(raw: string, now: Date): string | null {
  const norm = normalizeSearch(raw);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (norm === 'demain') {
    return toIsoLocal(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  }
  if (norm === 'apres-demain') {
    return toIsoLocal(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2));
  }
  if (norm in WEEKDAYS) {
    // Prochaine occurrence, jamais aujourd'hui.
    const delta = ((WEEKDAYS[norm]! - today.getDay() + 7) % 7) || 7;
    return toIsoLocal(new Date(today.getFullYear(), today.getMonth(), today.getDate() + delta));
  }
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/.exec(norm);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    let year = m[3] ? Number(m[3]) : today.getFullYear();
    let d = new Date(year, month - 1, day);
    // Rejette les dates impossibles (31/02 roule en mars).
    if (d.getDate() !== day || d.getMonth() !== month - 1) return null;
    if (!m[3] && d < today) {
      year += 1;
      d = new Date(year, month - 1, day);
      if (d.getDate() !== day || d.getMonth() !== month - 1) return null; // 29/02
    }
    return toIsoLocal(d);
  }
  return null;
}

export function parseTaskInput(text: string, ctx: ParseContext): ParsedTaskInput {
  const tokens = text.trim().split(/\s+/).filter(Boolean);

  const result: ParsedTaskInput = {
    title: '', priority: null, priorityRaw: null, dueDate: null, dueRaw: null,
    tagMatches: [], unknownTags: [], assigneeMatches: [],
  };
  if (tokens.length === 0) return result;

  // Date : uniquement le tout dernier jeton.
  const last = tokens[tokens.length - 1]!;
  const due = parseDateToken(last, ctx.now);
  if (due) {
    result.dueDate = due;
    result.dueRaw = last;
    tokens.pop();
  }

  const tagsByNorm = new Map(ctx.tags.map((t) => [normalizeSearch(t.name), t]));
  const titleTokens: string[] = [];

  for (const raw of tokens) {
    // Priorité !1-!4 — le dernier gagne.
    if (ctx.allowPriority !== false && /^![1-4]$/.test(raw)) {
      result.priority = Number(raw[1]);
      result.priorityRaw = raw;
      continue;
    }
    // Tag #mot.
    if (ctx.allowTags !== false && raw.length > 1 && raw.startsWith('#')) {
      const name = raw.slice(1);
      const found = tagsByNorm.get(normalizeSearch(name));
      if (found) {
        if (!result.tagMatches.some((t) => t.id === found.id)) {
          result.tagMatches.push({ id: found.id, name: found.name, raw });
        }
      } else if (!result.unknownTags.some((u) => normalizeSearch(u.name) === normalizeSearch(name))) {
        result.unknownTags.push({ name, raw });
      }
      continue;
    }
    // Assigné @mot — match UNIQUE, sinon texte. La forme à points
    // (@charles.annoni) désambiguïse les homonymes : chaque partie doit
    // être un mot du nom complet.
    if (raw.length > 1 && raw.startsWith('@')) {
      const parts = normalizeSearch(raw.slice(1)).split('.').filter(Boolean);
      const hits = ctx.collaborators.filter((c) => {
        const words = normalizeSearch(c.full_name).split(/\s+/);
        return parts.length > 0 && parts.every((p) => words.includes(p));
      });
      if (hits.length === 1) {
        const hit = hits[0]!;
        if (!result.assigneeMatches.some((a) => a.id === hit.id)) {
          result.assigneeMatches.push({ id: hit.id, full_name: hit.full_name, raw });
        }
        continue;
      }
      // 0 ou plusieurs → reste du texte.
    }
    titleTokens.push(raw);
  }

  result.title = titleTokens.join(' ');
  return result;
}

/**
 * Suggestions d'assignés pendant la frappe : `word` est le mot en cours
 * (dernier jeton du champ). Suggère sur préfixe de prénom OU de nom,
 * insensible casse/accents. `insert` est le jeton à insérer à la place du
 * mot tapé — le prénom, que parseTaskInput saura matcher.
 */
export function suggestAssignees<T extends { id: string; full_name: string }>(
  word: string,
  collaborators: T[],
): (T & { insert: string })[] {
  if (!word.startsWith('@') || word.length < 2) return [];
  const prefix = normalizeSearch(word.slice(1));
  return collaborators
    .filter((c) =>
      normalizeSearch(c.full_name)
        .split(/\s+/)
        .some((part) => part.startsWith(prefix)),
    )
    .slice(0, 5)
    .map((c) => {
      const words = normalizeSearch(c.full_name).split(/\s+/);
      const first = words[0] ?? '';
      // Prénom ambigu (homonymes) → jeton complet à points, que
      // parseTaskInput sait matcher de façon unique.
      const firstIsUnique =
        collaborators.filter((o) =>
          normalizeSearch(o.full_name).split(/\s+/).includes(first),
        ).length === 1;
      return { ...c, insert: `@${firstIsUnique ? first : words.join('.')}` };
    });
}

/** Couleur de la palette la moins utilisée par les tags existants. */
export function pickLeastUsedColor(
  palette: readonly string[],
  tags: { color: string }[],
): string {
  let best = palette[0] ?? '#64748b';
  let bestCount = Infinity;
  for (const color of palette) {
    const count = tags.filter((t) => t.color === color).length;
    if (count < bestCount) {
      best = color;
      bestCount = count;
    }
  }
  return best;
}
